import SystemOperations from "./SystemOperations";
import HostingExpirationAlerts from "./HostingExpirationAlerts";
import AdminWebsitePricing from "./AdminWebsitePricing";
// src/pages/Dashboard.jsx
import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ClaimFFL from "./ClaimFFL";
import { UserFFLClaims, AdminFFLClaims } from "./FFLClaims";
import Users from "./Users";
import SupportRequests from "./SupportRequests";
import AdminHostedPayments from "./AdminHostedPayments";
import DealerSupportInbox from "./DealerSupportInbox";
import DealerSubscribers from "./DealerSubscribers";
import Account from "./Account";
import UpdatePassword from "./UpdatePassword";
import SavedDealers from "./SavedDealers";
import UpdateEmail from "./UpdateEmail";
import FFL_TIPS from "../data/fflTips";
import Config from "../utils/Config";
import { getFflExpirationDaysLeft } from "../utils/functions";
import "../utils/fontawesome";
import "./Dashboard.css";

const ADMIN_LEVEL = Config?.USERS?.ADMIN_USER_LEVEL ?? 3;

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const hostingQuery=new URLSearchParams(location.search);
  const hostingClaim=/^[a-f0-9]{24}$/i.test(hostingQuery.get('hostingClaim')||'')?hostingQuery.get('hostingClaim'):null;
  const hostingAdminClaim=/^[a-f0-9]{24}$/i.test(hostingQuery.get('hostingAdminClaim')||'')?hostingQuery.get('hostingAdminClaim'):null;
  const [hostingSelection,setHostingSelection]=useState(hostingAdminClaim);

  const [isAdmin, setIsAdmin] = useState(() => (user?.userLevel ?? 0) >= ADMIN_LEVEL);
  const [lastLogin, setLastLogin] = useState(null);
  const [hasPendingClaims, setHasPendingClaims] = useState(false);
  const [expiringClaims, setExpiringClaims] = useState([]);
  const firstLoad = useRef(true);
  const [focusSupportForm, setFocusSupportForm] = useState(false);
  const [supportTicketId, setSupportTicketId] = useState(null);

  // claim flow state
  const [fflData, setFflData] = useState(() => location.state || {});

  // storage key per role
  const storageKey = useMemo(() => (isAdmin ? "activeTabAdmin" : "activeTab"), [isAdmin]);

  // role-specific default tab
  const defaultTab = useMemo(() => (isAdmin ? "admin-pending" : "dealer-overview"), [isAdmin]);

  // initial tab: claim-redirect wins, else last-saved per role, else default
  const [activeTab, setActiveTab] = useState(() => {
    if (location.state?.fflNumber && !localStorage.getItem("fflClaimedOnce")) return "claim-ffl";
    const savedUser = localStorage.getItem("activeTab") || "dealer-overview";
    const savedAdmin = localStorage.getItem("activeTabAdmin") || "admin-pending";
    return (user?.userLevel ?? 0) >= ADMIN_LEVEL ? savedAdmin : savedUser;
  });

  const openSupportInbox = useCallback(value => {
    if (value?.type === "hosting-cancellation") {
      setFocusSupportForm({
        subject: "Website Hosting Cancellation Request",
        message: `Please cancel website hosting only for ${value.dealerName}.${value.fflNumber ? `\nFFL number: ${value.fflNumber}` : ""}\nPlease preserve my free directory listing and dealership claim.`,
      });
      setActiveTab("dealer-overview");
      return;
    }
    setSupportTicketId(typeof value === "string" ? value : null);
    setActiveTab("dealer-support-inbox");
  }, []);

  /* ---------- Effects ---------- */

  // resolve admin if userLevel unknown
  useEffect(() => {
    if ((user?.userLevel ?? null) != null) {
      setIsAdmin(user.userLevel >= ADMIN_LEVEL);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USERS_IS_ADMIN}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        });
        const data = res.ok ? await res.json().catch(() => null) : null;
        setIsAdmin(Boolean(data?.isadmin));
      } catch { }
    })();
  }, [user]);

  // post-login redirect once
  useEffect(() => {
    const storedRedirect = localStorage.getItem("postLoginRedirect");
    if (!storedRedirect) return;
    const { fflNumber, fflName } = JSON.parse(storedRedirect);
    setActiveTab("claim-ffl");
    setFflData({ fflNumber, fflName });
    localStorage.removeItem("postLoginRedirect");
  }, []);

  // one-time activation when landing with claim state
  useEffect(() => {
    if (location.state?.fflNumber && !localStorage.getItem("fflClaimedOnce")) {
      setActiveTab("claim-ffl");
      setFflData(location.state);
      localStorage.setItem("activeTab", "claim-ffl");
      localStorage.setItem("fflClaimedOnce", "true");
    }
  }, [location.state]);

  // persist tab by role
  useEffect(() => {
    localStorage.setItem(storageKey, activeTab);
  }, [activeTab, storageKey]);

  // cross-tab sync (role-aware)
  useEffect(() => {
    const onStorage = () => {
      const updated = localStorage.getItem(storageKey) || defaultTab;
      if (activeTab !== "claim-ffl" || updated === activeTab || window.dispatchEvent(new Event("ffls:before-editor-leave", { cancelable: true }))) setActiveTab(updated);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey, defaultTab, activeTab]);

  // ensure correct saved tab once isAdmin resolves (don’t clobber claim-ffl)
  useEffect(() => {
    if (!firstLoad.current) return;
    if (activeTab === "claim-ffl") {
      firstLoad.current = false;
      return;
    }
    const desired = isAdmin&&hostingAdminClaim?'hosted-payments':localStorage.getItem(storageKey) || defaultTab;
    setActiveTab(desired);
    firstLoad.current = false;
  }, [isAdmin, storageKey, defaultTab, activeTab, hostingAdminClaim]);

  // last login
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.USER_GET_LAST_LOGIN}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        });
        const data = await res.json();
        if (data.lastLogin) setLastLogin(new Date(data.lastLogin).toLocaleString());
      } catch { }
    })();
  }, []);

  // user-only: pending check
  useEffect(() => {
    if (isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const url = new URL(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_MY_FFLS_BY_STATUS}`);
        url.searchParams.set("status", "Pending");
        const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } });
        const payload = await res.json().catch(() => null);
        const claims = Array.isArray(payload) ? payload : Array.isArray(payload?.claims) ? payload.claims : [];
        if (!cancelled) setHasPendingClaims(claims.length > 0);
      } catch {
        if (!cancelled) setHasPendingClaims(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  // user-only: expiring banners
  // user-only: expiring banners
  const checkExpiringFFLs = useCallback(async () => {
    try {
      const res = await fetch(`${Config.API.API_BASE_URL}${Config.ENDPOINTS.DEALERS_MY_FFLS_BY_STATUS}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });
      const data = await res.json().catch(() => []);
      const list = Array.isArray(data) ? data : (Array.isArray(data?.claims) ? data.claims : []);

      // Only keep APPROVED/LIVE FFLs expiring in the next 45 days (and not already expired)
      const soon = list.filter((c) => {
        const daysLeft = getFflExpirationDaysLeft(c?.expirationDate);
        if (!Number.isFinite(daysLeft)) return false;
        // stash for rendering so we don't recompute
        c.__daysLeft = daysLeft;
        return daysLeft >= 0 && daysLeft <= 45 && ["Approved", "Live"].includes(c.status);
      });

      setExpiringClaims(soon);
    } catch {
      setExpiringClaims([]);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) checkExpiringFFLs();
  }, [checkExpiringFFLs, isAdmin]);

  /* ---------- View ---------- */

  const adminTabs = {
    'Super Admin':['users','admin-pending','admin-verified','support-requests','website-pricing','hosted-payments','system-operations'],
    'Operations Admin':['admin-pending','admin-verified'],
    'Support Admin':['users','support-requests'],
    'Billing Admin':['website-pricing','hosted-payments'],
    'Content Admin':[]
  };
  const canAdminTab = key => (adminTabs[user?.adminRole]||[]).includes(key);
  const sidebarItems = isAdmin
    ? [
      { key: "account", label: "Account", icon: "user" },
      { key: "update-password", label: "Update Password", icon: "lock" },
      { key: "saved-dealers", label: "Saved Dealers", icon: "bookmark" },
      // { key: "update-email", label: "Update Email", icon: "envelope" },
      { key: "users", label: "Users", icon: "fa-users" },
      { key: "admin-pending", label: "Pending / Cancelled", icon: "hourglass-half" },
      { key: "admin-verified", label: "Verified Websites", icon: "fa-globe" },
      { key: "support-requests", label: "Support Requests", icon: "envelope" },
      { key: "website-pricing", label: "Website Pricing", icon: "fa-globe" },
      { key: "hosted-payments", label: "Website Payments", icon: "fa-globe" },
      ...((user?.userLevel??0)>=3?[{key:"system-operations",label:"System Operations",icon:"gauge"}]:[]),
    ]
    : [
      { key: "dealer-overview", label: "Dashboard", icon: "gauge" },
      { key: "account", label: "Account", icon: "user" },
      { key: "update-password", label: "Update Password", icon: "lock" },
      { key: "update-email", label: "Update Email", icon: "envelope" },
      { key: "saved-dealers", label: "Saved Dealers", icon: "bookmark" },
      ...(hasPendingClaims ? [{ key: "pending-claims", label: "Pending Claims", icon: "hourglass-half" }] : []),
      { key: "my-ffls", label: "My Dealerships", icon: "fa-globe" },
      { key: "dealer-subscribers", label: "Subscribers", icon: "envelope" },
      { key: "dealer-support-inbox", label: "Support Inbox", icon: "envelope" },
      // { key: "claim-ffl", label: "Claim / Renew FFL", icon: "fingerprint" },
    ];
  const visibleSidebarItems = sidebarItems.filter(item=>!isAdmin||!Object.values(adminTabs).flat().includes(item.key)||canAdminTab(item.key));
  useEffect(()=>{
    if(isAdmin&&Object.values(adminTabs).flat().includes(activeTab)&&!canAdminTab(activeTab))setActiveTab((adminTabs[user?.adminRole]||[])[0]||'account');
  },[isAdmin,user?.adminRole,activeTab]);

  return (
    <div className="dashboard-wrapper" id="dashboard-container">
      {/* Sidebar */}
      <div className="dashboard-menu">
        <h2>
          <span className="titleIcon"><FontAwesomeIcon icon={isAdmin ? "user-shield" : "gauge"} />&nbsp;&nbsp;
            {isAdmin ? "Admin" : "Welcome"},{" "}</span>{user?.firstName || "User"}
        </h2>

        {lastLogin && (
          <div className="last-login">
            <small><span className="titleIcon">Last Login:</span> {lastLogin}</small>
          </div>
        )}

        {/* User expiring banners */}
        {!isAdmin && expiringClaims.length > 0 && expiringClaims.map((claim) => {
          const daysLeft = Number.isFinite(claim.__daysLeft)
            ? claim.__daysLeft
            : getFflExpirationDaysLeft(claim.expirationDate);

          // Consider it a pending renewal ONLY if there is an actual upload attached
          const hasRenewalUpload = Boolean(
            claim.renewalUploaded ||
            claim.renewalFileId ||
            claim.renewalUploadAt ||
            claim.renewal?.fileId ||
            claim.renewal?.uploadedAt
          );

          const pendingRenew = hasRenewalUpload &&
            ["PendingApproval", "Pending", "UnderReview"].includes((claim.renewalStatus || "").toString());

          return (
            <div
              key={claim.dealerId}
              className={`alert-banner ${pendingRenew ? "alert-info" : daysLeft <= 15 ? "alert-danger" : "alert-warning"}`}
            >
              {pendingRenew ? (
                `✅ We’ve received your updated FFL for #${claim.fflNumber}. Awaiting approval.`
              ) : (
                <>
                  <span>⚠️ FFL #{claim.fflNumber} expires in {daysLeft} days.<br />Renew your license through ATF. Upload your updated FFL here to keep your dealership information current.</span>
                  <button
                    className="renew-button"
                    onClick={() => {
                      setActiveTab("claim-ffl");
                      setFflData({
                        fflNumber: claim.fflNumber,
                        fflName: claim.fflName,
                        claimId: claim._id,
                        dealerId: claim.dealerId,
                      });
                      localStorage.setItem("activeTab", "claim-ffl");
                      localStorage.setItem("fflClaimedOnce", "true");
                      localStorage.setItem("claimId", claim._id);
                      localStorage.setItem("dealerId", claim.dealerId);
                      localStorage.setItem("showRenewModal", true);
                    }}
                  >
                    Upload Updated FFL
                  </button>
                </>
              )}
            </div>
          );
        })}

        <ul id="dashboard-destinations">
          {visibleSidebarItems.map(({ key, label, icon }) => (
            <li key={key} className={activeTab === key ? "active" : ""}>
              <button onClick={() => {
                if (activeTab !== "claim-ffl" || key === activeTab || window.dispatchEvent(new Event("ffls:before-editor-leave", { cancelable: true }))) setActiveTab(key);
              }}>
                <span className="titleIcon"><FontAwesomeIcon icon={icon} /></span>&nbsp;&nbsp;{label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Main */}
      <div className="dashboard-content">
        <div className="dashboard-actions">
          <div className="card">
            {((isAdmin&&canAdminTab('hosted-payments')&&activeTab==='admin-pending')||(!isAdmin&&(activeTab==='dealer-overview'||hostingClaim)))&&<HostingExpirationAlerts key={String(user?.id||user?._id)} isAdmin={isAdmin} userId={user?.id||user?._id} targetClaimId={hostingClaim} refreshKey={activeTab} onSupport={openSupportInbox} onManage={claim=>{if(isAdmin){setHostingSelection(claim._id);setActiveTab('hosted-payments');}else if(hostingClaim===claim._id){if(activeTab==='claim-ffl'&&!window.dispatchEvent(new Event('ffls:before-editor-leave',{cancelable:true})))return;localStorage.removeItem('fflClaimedOnce');localStorage.setItem('fflEditData',true);localStorage.setItem('claimId',claim._id);localStorage.setItem('dealerId',claim.dealerId);navigate('/dashboard',{state:{activeTab:'claim-ffl',setupSection:'dealerSite',fflNumber:claim.fflNumber,fflName:claim.fflName,claimId:claim._id,dealerId:claim.dealerId}});}else{navigate('/dashboard?hostingClaim='+encodeURIComponent(claim._id));}}}/>}
            {/* Shared */}
            {!isAdmin && activeTab === "dealer-subscribers" && <DealerSubscribers />}
            {activeTab === "account" && <Account isAdmin={isAdmin} />}
            {activeTab === "update-password" && <UpdatePassword />}
            {activeTab === "update-email" && <UpdateEmail />}
            {activeTab === "saved-dealers" && <SavedDealers isAdmin={isAdmin} />}

            {/* User */}
            {!isAdmin && activeTab === "dealer-support-inbox" && <DealerSupportInbox initialTicketId={supportTicketId} onNewRequest={() => { setFocusSupportForm(true); setActiveTab("dealer-overview"); }} />}
            {!isAdmin && activeTab === "dealer-overview" && <div className="login-container login-container-dashboard"><div className="login-box"><UserFFLClaims overview status="Pending,Approved,Live" onOpenSupportInbox={openSupportInbox} focusSupportForm={focusSupportForm} onSupportFocused={() => setFocusSupportForm(false)} /></div></div>}
            {!isAdmin && activeTab === "claim-ffl" && (
              <ClaimFFL
                key={fflData.dealerId}
                fflData={fflData}
                onClaimSuccess={() => setHasPendingClaims(true)}
                onUploadSuccess={checkExpiringFFLs}
              />
            )}
            {!isAdmin && activeTab === "pending-claims" && (
              <UserFFLClaims status="Pending" tips={FFL_TIPS} />
            )}
            {!isAdmin && activeTab === "my-ffls" && (
              <UserFFLClaims status="Approved,Live" tips={FFL_TIPS} onOpenSupportInbox={openSupportInbox} />
            )}

            {/* Admin */}
            {isAdmin && (user?.userLevel??0)>=3 && activeTab === "system-operations" && canAdminTab("system-operations") && <SystemOperations />}
            {isAdmin && activeTab === "website-pricing" && canAdminTab("website-pricing") && <AdminWebsitePricing />}
            {isAdmin && activeTab === "hosted-payments" && canAdminTab("hosted-payments") && <AdminHostedPayments key={hostingSelection||"payments"} initialClaimId={hostingSelection} />}
            {isAdmin && activeTab === "support-requests" && canAdminTab("support-requests") && <SupportRequests isAdmin={isAdmin} />}
            {isAdmin && activeTab === "users" && canAdminTab("users") && <Users mode={isAdmin ? "admin" : "user"} />}
            {isAdmin && activeTab === "admin-pending" && canAdminTab("admin-pending") && (
              <AdminFFLClaims status="Pending,Cancelled" />
            )}
            {isAdmin && activeTab === "admin-verified" && canAdminTab("admin-verified") && (
              <AdminFFLClaims status="Approved,Live" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
